import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaLock, FaEnvelope, FaArrowRight, FaSkating } from 'react-icons/fa';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { formatApiError } from '../utils/errorUtils';
import logo from '../assets/logo.png';
import './AuthPages.css';

function StudentLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await login(email, password);
      navigate(res.redirectPath || (res.determinedRole === 'admin' ? '/admin/dashboard' : '/dashboard'));
    } catch (err) {
      setError(err.message || 'Access Denied. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setResetMessage('');
    setResetLoading(true);

    try {
      const response = await authAPI.requestPasswordReset({
        email: resetEmail || email,
        accountType: 'user'
      });
      setResetMessage(response.data?.message || 'If that email exists, a password reset link has been sent.');
    } catch (err) {
      setError(formatApiError(err, 'Failed to send reset email'));
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="auth-shell student-auth-wrapper">
      <div className="blur-circle circle-1" />
      <div className="blur-circle circle-2" />

      <motion.div
        className="auth-card-modern student-theme"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Link
          to="/"
          className="top-right-back-btn"
          title="Go to Home"
          aria-label="Go to Home"
        >
          <ArrowLeft size={16} strokeWidth={2.5} />
          <span>Back</span>
        </Link>

        <div className="auth-header-student">
          <div className="student-badge">
            <FaSkating /> <span>User Portal</span>
          </div>
          <div className="auth-brand-block">
            <img src={logo} alt="Epsilon logo" className="auth-brand-logo" />
            <div>
              <h2>EPSILON <span>SKATING</span></h2>
            </div>
          </div>
        </div>

        {error && (
          <motion.div
            className="error-toast"
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
          >
            {error}
          </motion.div>
        )}
        {resetMessage && <div className="success-toast">{resetMessage}</div>}

        <form onSubmit={handleSubmit} className="modern-form">
          <div className="input-container">
            <label><FaEnvelope /> Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@email.com"
              required
              disabled={loading}
            />
          </div>

          <div className="input-container">
            <label><FaLock /> Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              disabled={loading}
            />
          </div>

          <button type="submit" className="btn-student-login" disabled={loading}>
            {loading ? (
              <span className="loader-dots">Verifying...</span>
            ) : (
              <>Login <FaArrowRight /></>
            )}
          </button>
        </form>

        <button type="button" className="forgot-toggle" onClick={() => setShowForgotPassword((prev) => !prev)}>
          {showForgotPassword ? 'Hide forgot password' : 'Forgot password?'}
        </button>

        {showForgotPassword && (
          <form onSubmit={handleForgotPassword} className="forgot-panel">
            <div className="input-container">
              <label><FaEnvelope /> Reset Email</label>
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Enter your registered email"
                required
                disabled={resetLoading}
              />
            </div>
            <button type="submit" className="btn-inactive forgot-submit" disabled={resetLoading}>
              {resetLoading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        )}

        <div className="auth-footer-links">
          <p>New to Epsilon? <Link to="/register">Create Account</Link></p>
        </div>
      </motion.div>
    </div>
  );
}

export default StudentLoginPage;
